import { Link } from 'react-router-dom';

export default function GuideSection() {
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10 md:mb-14">
            <div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                Shopping Guides
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-md">
                Learn everything about CSSBuy spreadsheets, shopping agents, and how to get the best deals from China.
              </p>
            </div>
            <Link
              to="/article/cssbuy-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Guides
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>

          {/* Guide cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {/* Main featured card */}
            <Link
              to="/article/cssbuy-spreadsheet-complete-guide"
              className="md:col-span-1 group relative rounded-xl overflow-hidden bg-foreground-800 cursor-pointer"
            >
              <div className="aspect-[4/5] md:aspect-auto md:h-full relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Person%20sitting%20at%20sunlit%20cafe%20table%20with%20open%20laptop%20showing%20colorful%20charts%2C%20surrounded%20by%20hanging%20plants%20and%20exposed%20brick%20wall%2C%20creative%20freelancer%20lifestyle%2C%20warm%20afternoon%20light%20streaming%20through%20windows%2C%20urban%20bohemian%20aesthetic%2C%20focused%20productive%20atmosphere&width=900&height=1200&seq=guide-main-v2&orientation=portrait"
                  alt="CSSBuy Spreadsheet Complete Guide"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/80 via-foreground-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-semibold mb-3">Featured</span>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-background-50">
                    CSSBuy Spreadsheet Complete Guide 2026
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-background-200 leading-relaxed">
                    Everything you need to know about using CSSBuy spreadsheets — from finding products to placing orders safely.
                  </p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-background-50 whitespace-nowrap">
                    Read Guide
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                </div>
              </div>
            </Link>

            {/* Right side cards */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {/* Card 1 */}
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                className="group bg-background-50 rounded-xl p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/80 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">New</span>
                  <span className="text-xs text-foreground-400">5 min read</span>
                </div>
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Open%20leather%20journal%20with%20handwritten%20shopping%20list%20and%20product%20sketches%20next%20to%20a%20steaming%20coffee%20cup%2C%20pencil%20resting%20on%20page%2C%20creative%20planning%20session%2C%20warm%20overhead%20lamp%20lighting%2C%20analog%20organization%20aesthetic&width=800&height=450&seq=guide-card1-v2&orientation=landscape"
                    alt="How to Use CSSBuy Spreadsheet"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900">How CSSBuy Spreadsheets Work</h4>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed flex-1">
                  A beginner-friendly walkthrough on navigating and using CSSBuy spreadsheets effectively.
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 mt-3 whitespace-nowrap">
                  Read More
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </span>
              </Link>

              {/* Card 2 */}
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                className="group bg-background-50 rounded-xl p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/80 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold">Guide</span>
                  <span className="text-xs text-foreground-400">7 min read</span>
                </div>
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Two%20smartphones%20placed%20side%20by%20side%20on%20white%20desk%20showing%20different%20shopping%20app%20screens%2C%20comparison%20and%20choice%20concept%2C%20clean%20tech%20lifestyle%20photography%2C%20bright%20even%20lighting%2C%20split%20screen%20decision%20making%20visual&width=800&height=450&seq=guide-card2-v2&orientation=landscape"
                    alt="CSSBuy vs Kakobuy comparison"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900">CSSBuy vs Kakobuy Spreadsheet</h4>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed flex-1">
                  Compare the two most popular shopping agents and their spreadsheet ecosystems side by side.
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 mt-3 whitespace-nowrap">
                  Read More
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </span>
              </Link>

              {/* Card 3 */}
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                className="group bg-background-50 rounded-xl p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/80 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">Popular</span>
                  <span className="text-xs text-foreground-400">6 min read</span>
                </div>
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stack%20of%20dollar%20bills%20fanned%20out%20next%20to%20a%20stylish%20pair%20of%20sunglasses%20and%20a%20trendy%20cap%20on%20dark%20wood%20surface%2C%20budget%20luxury%20concept%2C%20wealth%20and%20style%20photography%2C%20rich%20golden%20lighting%2C%20aspirational%20lifestyle%20composition&width=800&height=450&seq=guide-card3-v2&orientation=landscape"
                    alt="Budget spreadsheet finds"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900">Best Budget Spreadsheet Finds</h4>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed flex-1">
                  Top affordable picks from CSSBuy spreadsheets that look expensive but cost under $50.
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 mt-3 whitespace-nowrap">
                  Read More
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </span>
              </Link>

              {/* Card 4 */}
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                className="group bg-background-50 rounded-xl p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/80 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">Safety</span>
                  <span className="text-xs text-foreground-400">8 min read</span>
                </div>
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Brass%20padlock%20with%20key%20inserted%20resting%20on%20stack%20of%20colorful%20shopping%20bags%20from%20different%20stores%2C%20security%20and%20retail%20concept%2C%20bright%20studio%20lighting%20on%20white%20backdrop%2C%20consumer%20protection%20theme%2C%20clean%20commercial%20photography&width=800&height=450&seq=guide-card4-v2&orientation=landscape"
                    alt="Safe shopping guide"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900">Safe Shopping with Spreadsheets</h4>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed flex-1">
                  Essential safety tips to avoid scams and shop with confidence using CSSBuy and other agents.
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 mt-3 whitespace-nowrap">
                  Read More
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}